import axios from 'axios';

// Base URL for API requests - ensure trailing slash
const API_BASE_URL = '/api';

// Simple cache for GET requests
interface CacheEntry {
  data: any;
  timestamp: number;
  expiry: number; // Time in milliseconds when this entry expires
}

const apiCache: Record<string, CacheEntry> = {};
const DASHBOARD_TOKEN_STORAGE_KEY = 'dashboard_auth_token';
export const DASHBOARD_AUTH_CHANGED_EVENT = 'dashboard-auth-changed';

export const getDashboardToken = (): string => {
  try {
    return localStorage.getItem(DASHBOARD_TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

export const setDashboardToken = (token: string | null) => {
  try {
    if (!token) {
      localStorage.removeItem(DASHBOARD_TOKEN_STORAGE_KEY);
    } else {
      localStorage.setItem(DASHBOARD_TOKEN_STORAGE_KEY, token);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(DASHBOARD_AUTH_CHANGED_EVENT, {
          detail: { hasToken: Boolean(token) },
        })
      );
    }
  } catch {
    // no-op for non-browser contexts
  }
};

// Cache expiry times (in milliseconds)
const CACHE_EXPIRY = {
  default: 60 * 1000, // 1 minute
  sites: 5 * 60 * 1000, // 5 minutes
  profiles: 10 * 60 * 1000, // 10 minutes
};

// Create an axios instance with the base URL
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Minimal logging to prevent console spam
// Only log errors and important operations
const IMPORTANT_ENDPOINTS = ['/chat/', '/crawl/'];

// Add request interceptor with caching for GET requests
apiClient.interceptors.request.use(async (request) => {
  const token = getDashboardToken();
  const isDashboardRoute = request.url?.startsWith('/dashboard/');
  const isCrawlRoute = request.url?.includes('/crawl/');
  if (token && (isDashboardRoute || isCrawlRoute)) {
    request.headers = request.headers || {};
    request.headers.Authorization = `Bearer ${token}`;
  }

  // Only log POST/PUT/DELETE requests or important endpoints
  const isImportantEndpoint = IMPORTANT_ENDPOINTS.some(endpoint => 
    request.url?.includes(endpoint)
  );
  
  if (request.method !== 'get' || isImportantEndpoint) {
    console.log(`API Request: ${request.method?.toUpperCase()} ${request.url}`);
  }

  const isDashboardEndpoint = request.url?.startsWith('/dashboard/');

  // Check cache for GET requests (skip auth-sensitive dashboard endpoints)
  if (request.method === 'get' && request.url && !isDashboardEndpoint) {
    const cacheKey = `${request.url}${JSON.stringify(request.params || {})}`;
    const cachedResponse = apiCache[cacheKey];
    
    if (cachedResponse && Date.now() < cachedResponse.expiry) {
      // Return cached response
      console.log(`Using cached response for ${request.url}`);
      
      // Create a new Promise that resolves with the cached data
      return Promise.resolve({
        ...request,
        adapter: () => {
          return Promise.resolve({
            data: cachedResponse.data,
            status: 200,
            statusText: 'OK',
            headers: {},
            config: request,
            request: request
          });
        }
      });
    }
  }
  
  return request;
});

// Add response interceptor with caching
apiClient.interceptors.response.use(
  response => {
    // Only log responses for non-GET requests or important endpoints
    const isImportantEndpoint = IMPORTANT_ENDPOINTS.some(endpoint => 
      response.config.url?.includes(endpoint)
    );
    
    if (response.config.method !== 'get' || isImportantEndpoint) {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    
    // Cache successful GET responses
    const isDashboardEndpoint = response.config.url?.startsWith('/dashboard/');
    if (response.config.method === 'get' && response.config.url && !isDashboardEndpoint) {
      const url = response.config.url;
      const cacheKey = `${url}${JSON.stringify(response.config.params || {})}`;
      
      // Determine cache expiry based on endpoint
      let expiryTime = CACHE_EXPIRY.default;
      if (url.includes('/sites/')) {
        expiryTime = CACHE_EXPIRY.sites;
      } else if (url.includes('/profiles/')) {
        expiryTime = CACHE_EXPIRY.profiles;
      }
      
      // Store in cache
      apiCache[cacheKey] = {
        data: response.data,
        timestamp: Date.now(),
        expiry: Date.now() + expiryTime
      };
    }
    
    return response;
  },
  error => {
    console.error('API Error:', 
      error.response?.status || 'Network Error', 
      error.config?.url || 'Unknown URL',
      error.response?.data || error.message
    );
    return Promise.reject(error);
  }
);

// Site interfaces
export interface Site {
  id: number;
  name: string;
  url: string;
  description: string;
  page_count: number;
  created_at: string;
}

// Page interfaces
export interface Page {
  id: number;
  site_id: number;
  url: string;
  title?: string;
  content?: string;
  summary?: string;
  metadata?: any;
  is_chunk?: boolean;
  chunk_index?: number | null;
  parent_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  is_parent?: boolean;
  parent_title?: string;
}

// Search interfaces
export interface SearchResult {
  id: number;
  url: string;
  title: string;
  content?: string;
  snippet?: string;
  summary?: string;
  similarity: number;
  context?: string | null;
  is_chunk: boolean;
  chunk_index: number | null;
  parent_id: number | null;
  parent_title?: string | null;
  site_id: number;
  site_name?: string;
}

export interface SearchResponse {
  results: SearchResult[];
  count: number;
  query: string;
  threshold: number;
  use_embedding: boolean;
}

// Crawl interfaces
export interface CrawlRequest {
  url: string;
  site_name?: string;
  site_description?: string;
  is_sitemap?: boolean;
  max_urls?: number;
}

export interface CrawlResponse {
  site_id: number;
  site_name: string;
  url: string;
  message: string;
  status: string;
  next_steps: {
    check_status: string;
    view_pages: string;
    search_content: string;
  };
}

export interface CrawlStatus {
  site_id: number;
  site_name: string;
  name?: string;
  url: string;
  page_count: number;
  chunk_count: number;
  total_count: number;
  created_at: string;
  updated_at: string;
  status?: string;
  progress?: number;
  pages_crawled?: number;
  pages_found?: number;
  max_pages?: number;
  depth?: number;
  follow_external_links?: boolean;
  error?: string;
  next_steps: {
    view_pages: string;
    search_content: string;
  };
}

// Chat interfaces
export interface ChatMessage {
  id: string;
  role: string;
  content: string;
  created_at?: string;
  context?: string;
}

export interface ChatRequest {
  message: string;
  session_id?: string;
  user_id?: string;
  profile?: string;
}

export interface ChatResponse {
  response: string;
  session_id: string;
  user_id: string | null;
  context?: SearchResult[];
  conversation_history?: ChatMessage[];
}

export interface Profile {
  name: string;
  description: string;
}

export interface ProfilesResponse {
  profiles: Profile[];
  active_profile: string;
}

export interface UserPreference {
  id?: number;
  user_id: string;
  preference_type: string;
  preference_value: string;
  context?: string;
  confidence: number;
  created_at?: string;
  updated_at?: string;
  last_used?: string;
  source_session?: string;
  is_active: boolean;
  metadata?: any;
}

export interface UserPreferenceResponse {
  preferences: UserPreference[];
  count: number;
  user_id: string;
}

export interface SiteUsageSummaryTotals {
  request_count: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

export interface DashboardSiteInsights {
  site_id: number;
  month: string;
  usage: {
    request_count: number;
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens: number;
    /** Customer-facing bill: all tokens at most-expensive model × markup */
    billed_cost_usd?: number;
    billed_cost_usd_micros?: number;
    billing_model?: string | null;
    billing_markup?: number;
    /** Platform-admin only: sum of per-model estimated spend */
    actual_cost_usd?: number;
    actual_cost_usd_micros?: number;
  };
  bookings: {
    this_month: number;
    last_month: number;
    mom_pct: number | null;
    upcoming: number;
    cancelled_this_month: number;
  };
  estimated_value: {
    currency: string;
    amount: number;
    bookings_with_price: number;
    bookings_without_price: number;
    by_currency: Array<{ currency: string; amount: number }>;
  };
  activity: {
    leads_captured: number;
    handoffs_completed: number;
    reviews_positive: number;
    outcomes_negative: number;
    lead_to_booking_pct: number | null;
  };
}

export interface DashboardSiteBilling {
  site_id?: number;
  user_id?: number;
  scope?: 'site' | 'account';
  requires_prepaid_balance: boolean;
  balance_usd: number;
  balance_usd_micros: number;
  chat_enabled: boolean;
  chatbot_running?: boolean;
  payment_required: boolean;
  hourly_runtime_rate_usd?: number;
  hourly_runtime_billing_active?: boolean;
  running_hours_total?: number;
  chatbot_stopped_at?: string | null;
  chat_data_retention_days?: number;
  chat_data_deletion_at?: string | null;
  days_until_chat_data_deletion?: number | null;
  auto_recharge_enabled: boolean;
  auto_recharge_threshold_usd: number | null;
  auto_recharge_amount_usd: number | null;
  has_payment_method: boolean;
  stripe_customer_id?: string | null;
  last_activity_at?: string | null;
  stripe_configured?: boolean;
  /** True when STRIPE_SECRET_KEY is sk_test_… (demo money) */
  stripe_test_mode?: boolean;
}

export interface SiteUsageSummaryMonthlyResponse {
  website_id: number;
  usage_month: string;
  totals: SiteUsageSummaryTotals;
  by_model: Array<
    SiteUsageSummaryTotals & {
      model: string;
    }
  >;
}

export interface SiteUsageSummaryRangeResponse {
  website_id: number;
  months: number;
  series: Array<
    SiteUsageSummaryTotals & {
      usage_month: string;
    }
  >;
}

export interface DashboardUser {
  id: number;
  email: string;
  full_name?: string;
  is_platform_admin: boolean;
  is_active?: boolean;
  must_reset_password?: boolean;
}

export interface DashboardSite {
  id: number;
  name: string;
  url: string;
  role: string;
  page_count?: number;
}

export interface DashboardSiteProfile {
  id: number;
  name: string;
  url: string;
  description: string;
  page_count: number;
  parent_page_count: number;
  total_page_count: number;
  updated_at?: string | null;
  last_crawl_started_at?: string | null;
}

export interface DashboardCrawlStatus {
  site_id: number;
  site_name: string;
  url: string;
  page_count: number;
  parent_page_count: number;
  chunk_count: number;
  total_count: number;
  created_at?: string;
  updated_at?: string;
  last_crawl_started_at?: string | null;
  last_crawl_completed_at?: string | null;
  crawl_in_progress?: boolean;
  status?: string;
}

export interface DashboardCrawlOptionsPayload {
  crawl_type: 'url' | 'linked' | 'sitemap';
  max_pages: number;
  follow_external_links: boolean;
  include_patterns: string[];
  exclude_patterns: string[];
  follow_redirects: boolean;
  max_depth: number;
}

export interface DashboardClaimSitePayload extends DashboardCrawlOptionsPayload {
  url: string;
  site_name?: string;
  site_description?: string;
}

export interface DashboardKnowledgeEntry {
  entry_id: string;
  question: string;
  answer: string;
  summary?: string;
  updated_at?: string | null;
  created_at?: string | null;
}

export interface DashboardLoginResponse {
  token: string;
  expires_at: string;
  user: DashboardUser;
}

export interface DashboardRevenueConfigResponse {
  site_id: number;
  config: {
    plan_tier: string;
    default_language?: string;
    capabilities: Record<string, any>;
    provider_options: Record<string, any>;
    automation: Record<string, any>;
    booking_policy?: Record<string, any>;
    post_booking?: Record<string, any>;
    observability?: Record<string, any>;
  };
}

export interface DashboardBooking {
  id: number;
  start_at: string;
  end_at: string;
  slot_label: string;
  timezone: string;
  resource_key: string;
  booked_to: string;
  booked_for: string;
  lead_name: string;
  lead_email: string;
  lead_company: string;
  lead_phone?: string;
  lead_address?: string;
  lead_job_title?: string;
  page_url?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  booking_price?: string;
  booking_price_currency?: string;
  calendar_provider?: string;
  external_event_id?: string;
  calendar_sync_status?: string;
  calendar_sync_error?: string;
  calendar_html_link?: string;
  status: string;
  source: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardCalendarConnection {
  connected: boolean;
  provider: 'google' | 'calendly' | null;
  connected_account: string;
  calendar_id: string;
  event_type_uri: string;
  scheduling_url: string;
  needs_setup: boolean;
  last_error: string;
}

export interface DashboardEmailConnection {
  connected: boolean;
  provider: 'google' | string;
  connected_account: string;
}

export interface DashboardCalendarOption {
  id: string;
  name: string;
  scheduling_url?: string;
}

export interface DashboardEmbedInfo {
  site_id: number;
  site_name: string;
  site_url: string;
  website_id: number;
  api_base_url: string;
  chat_ui_test_url: string;
  script_snippet_default: string;
  iframe_snippet_default: string;
  script_snippet_custom: string;
  iframe_snippet_custom: string;
  script_snippet: string;
  iframe_snippet: string;
  is_customized: boolean;
  widget_settings_custom: Record<string, any>;
  widget_customization_enabled: boolean;
  notes: {
    website_id_param: string;
    /** Present only for platform admins */
    internal_api_key_required?: boolean;
    /** Present only for platform admins */
    internal_api_key_hint?: string;
  };
}

// API service
export const apiService = {
  // Site methods
  getSites: async (): Promise<Site[]> => {
    try {
      const response = await apiClient.get('/sites');
      console.log('Raw sites response:', response.data);
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.sites && Array.isArray(response.data.sites)) {
        // This is the expected format from the API: { sites: [...], count: number }
        console.log('Found sites array in response:', response.data.sites);
        return response.data.sites;
      } else if (response.data && typeof response.data === 'object') {
        // Try to extract sites from the response object
        const possibleSites = Object.values(response.data).find(val => Array.isArray(val));
        if (possibleSites && Array.isArray(possibleSites)) {
          console.log('Found possible sites array in response:', possibleSites);
          return possibleSites as Site[];
        }
      }
      
      // If we can't determine the format, return an empty array
      console.error('Unexpected sites response format:', response.data);
      return [];
    } catch (error) {
      console.error('Error fetching sites:', error);
      throw error;
    }
  },

  getSite: async (siteId: number): Promise<Site> => {
    try {
      const response = await apiClient.get(`/sites/${siteId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting site ${siteId}:`, error);
      throw error;
    }
  },

  getSitePages: async (siteId: number, includeChunks: boolean = false): Promise<Page[] | { pages: Page[] }> => {
    try {
      const response = await apiClient.get(`/sites/${siteId}/pages`, {
        params: { include_chunks: includeChunks }
      });
      
      console.log('Raw site pages response:', response.data);
      
      // Return the data as is, let the component handle the format
      return response.data;
    } catch (error) {
      console.error(`Error getting pages for site ${siteId}:`, error);
      throw error;
    }
  },

  getPageById: async (pageId: number): Promise<Page> => {
    try {
      const response = await apiClient.get(`/pages/${pageId}`);
      console.log('Page by ID response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error getting page ${pageId}:`, error);
      throw error;
    }
  },

  getPageChunks: async (pageId: number): Promise<Page[]> => {
    try {
      const response = await apiClient.get(`/pages/${pageId}/chunks`);
      console.log('Page chunks response:', response.data);
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.chunks && Array.isArray(response.data.chunks)) {
        return response.data.chunks;
      } else {
        console.error('Unexpected page chunks response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error(`Error getting chunks for page ${pageId}:`, error);
      throw error;
    }
  },

  // Crawl methods
  startCrawl: async (crawlRequest: any): Promise<CrawlStatus> => {
    try {
      const response = await apiClient.post('/crawl/', crawlRequest);
      
      // Check if the response contains a site_id
      if (response.data && response.data.site_id) {
        // Return the crawl status
        return response.data;
      } else {
        throw new Error('Invalid response from crawl API');
      }
    } catch (error) {
      console.error('Error starting crawl:', error);
      throw error;
    }
  },

  getCrawlStatus: async (siteId?: string | number): Promise<CrawlStatus | CrawlStatus[]> => {
    try {
      // If no siteId is provided, try to get all sites instead
      if (!siteId) {
        console.log('No siteId provided, fetching all sites instead');
        return apiService.getSites() as unknown as CrawlStatus[];
      }
      
      // Use the correct endpoint format with siteId
      const endpoint = `/crawl/status/${siteId}/`;
      
      console.log('Fetching crawl status from:', endpoint);
      const response = await apiClient.get(endpoint);
      console.log('Crawl status response:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching crawl status:', error);
      throw error;
    }
  },

  // Search methods
  search: async (
    query: string, 
    siteId?: number, 
    threshold: number = 0.3, 
    limit: number = 10,
    textOnly: boolean = false
  ): Promise<SearchResult[]> => {
    try {
      const response = await apiClient.get('/search/', {
        params: {
          query,
          site_id: siteId,
          threshold,
          limit,
          text_only: textOnly
        }
      });
      
      // Handle both array response and SearchResponse object
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        return response.data.results;
      } else {
        console.error('Unexpected search response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error searching:', error);
      throw error;
    }
  },

  // Chat methods
  sendMessage: async (message: string, profile: string, sessionId?: string, userName?: string): Promise<ChatMessage> => {
    try {
      const payload: any = { 
        message, 
        profile
      };
      
      // Use the user's name as the user_id if provided, otherwise use the profile name
      if (userName) {
        payload.user_id = userName;
      }
      
      // Add session_id if provided
      if (sessionId) {
        payload.session_id = sessionId;
      }
      
      // Add query parameters to always include context and search settings
      const params = {
        include_context: true,  // Always include search context
        result_limit: 10,       // Reasonable default
        similarity_threshold: 0.5 // Reasonable default
      };
      
      const response = await apiClient.post('/chat/', payload, { params });
      
      console.log('Chat response:', response.data);
      
      // Handle different response formats
      if (response.data && typeof response.data === 'object') {
        if (response.data.response) {
          // Standard format: { response: string, session_id: string, ... }
          const assistantMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'assistant',
            content: response.data.response,
            created_at: new Date().toISOString()
          };
          
          // If context was included, add it to the message
          if (response.data.context) {
            assistantMessage.context = response.data.context;
          }
          
          return assistantMessage;
        } else if (response.data.content) {
          // Message format: { role: string, content: string, ... }
          const message: ChatMessage = {
            id: Date.now().toString(),
            role: response.data.role || 'assistant',
            content: response.data.content,
            created_at: new Date().toISOString()
          };
          
          // If context was included, add it to the message
          if (response.data.context) {
            message.context = response.data.context;
          }
          
          return message;
        }
      }
      
      // Fallback for unexpected formats
      console.error('Unexpected chat response format:', response.data);
      return {
        id: Date.now().toString(),
        role: 'assistant',
        content: typeof response.data === 'string' 
          ? response.data 
          : 'Received an unexpected response format. Please try again.',
        created_at: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  getProfiles: async (sessionId?: string): Promise<Profile[]> => {
    try {
      const params: any = {};
      
      // Add session_id if provided
      if (sessionId) {
        params.session_id = sessionId;
      }
      
      // Use URL without trailing slash
      const response = await apiClient.get('/chat/profiles', { params });
      
      // Handle both array response and ProfilesResponse object
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.profiles)) {
        return response.data.profiles;
      } else {
        console.error('Unexpected profiles response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error getting profiles:', error);
      // Return empty array instead of throwing to prevent UI errors
      return [];
    }
  },

  setProfile: async (profileId: string, sessionId?: string, userName?: string): Promise<void> => {
    try {
      const params: any = {};
      
      // Use the user's name as the user_id if provided
      if (userName) {
        params.user_id = userName;
      }
      
      // Add session_id if provided
      if (sessionId) {
        params.session_id = sessionId;
      }
      
      // Use URL without trailing slash
      await apiClient.post(`/chat/profiles/${profileId}`, null, { params });
      
      // Clear any cached chat history to ensure fresh data with the new profile
      if (sessionId) {
        const cacheKey = `/chat/history/${JSON.stringify({ session_id: sessionId })}`;
        if (apiCache[cacheKey]) {
          delete apiCache[cacheKey];
          console.log('Cleared chat history cache after profile change');
        }
      }
    } catch (error) {
      console.error('Error setting profile:', error);
      // Don't throw to prevent UI errors
    }
  },

  getChatHistory: async (sessionId: string): Promise<ChatMessage[]> => {
    try {
      // Use the correct endpoint format with query parameters
      const response = await apiClient.get('/chat/history', {
        params: { session_id: sessionId }
      });
      
      let messages: ChatMessage[] = [];
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        // Format: ChatMessage[]
        messages = response.data;
      } else if (response.data && response.data.messages && Array.isArray(response.data.messages)) {
        // Format: { messages: ChatMessage[], count: number, ... }
        messages = response.data.messages;
      } else if (response.data && typeof response.data === 'object') {
        // Try to extract messages from the response object
        const possibleMessages = Object.values(response.data).find(val => Array.isArray(val));
        if (possibleMessages && Array.isArray(possibleMessages)) {
          messages = possibleMessages as ChatMessage[];
        }
      }
      
      // Don't filter out system messages that contain search context
      return messages;
    } catch (error) {
      console.error('Error getting chat history:', error);
      // Return empty array instead of throwing to prevent UI errors
      return [];
    }
  },

  clearChatHistory: async (sessionId: string): Promise<void> => {
    try {
      await apiClient.delete('/chat/history', {
        params: { session_id: sessionId }
      });
      
      // Clear the cache for this session's chat history
      // This prevents old messages from reappearing after clearing
      const cacheKey = `/chat/history/${JSON.stringify({ session_id: sessionId })}`;
      if (apiCache[cacheKey]) {
        delete apiCache[cacheKey];
        console.log('Cleared chat history cache for session:', sessionId);
      }
    } catch (error) {
      console.error('Error clearing chat history:', error);
      throw error;
    }
  },

  getSiteMonthlyUsageSummary: async (siteId: number, month?: string): Promise<SiteUsageSummaryMonthlyResponse> => {
    try {
      const response = await apiClient.get('/chat/usage/summary/monthly', {
        params: {
          website_id: siteId,
          month: month || undefined,
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting monthly usage summary for site ${siteId}:`, error);
      throw error;
    }
  },

  getSiteMonthlyUsageSummaryRange: async (
    siteId: number,
    months: number = 6
  ): Promise<SiteUsageSummaryRangeResponse> => {
    try {
      const response = await apiClient.get('/chat/usage/summary/monthly/range', {
        params: {
          website_id: siteId,
          months,
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting usage summary range for site ${siteId}:`, error);
      throw error;
    }
  },

  // User Preferences methods
  getUserPreferences: async (userId: string, minConfidence: number = 0.7, activeOnly: boolean = true): Promise<UserPreference[]> => {
    try {
      const response = await apiClient.get('/chat/preferences', {
        params: {
          user_id: userId,
          min_confidence: minConfidence,
          active_only: activeOnly
        }
      });
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.preferences)) {
        return response.data.preferences;
      } else {
        console.error('Unexpected preferences response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error getting user preferences:', error);
      return [];
    }
  },

  addUserPreference: async (
    userId: string, 
    preferenceType: string, 
    preferenceValue: string, 
    context?: string, 
    confidence: number = 0.9,
    sessionId?: string
  ): Promise<UserPreference | null> => {
    try {
      const response = await apiClient.post('/chat/preferences', {
        preference_type: preferenceType,
        preference_value: preferenceValue,
        context: context || 'Manually added via UI',
        confidence: confidence,
        source_session: sessionId
      }, {
        params: { user_id: userId }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error adding user preference:', error);
      return null;
    }
  },

  deleteUserPreference: async (userId: string, preferenceId: number): Promise<boolean> => {
    try {
      await apiClient.delete(`/chat/preferences/${preferenceId}`, {
        params: { user_id: userId }
      });
      return true;
    } catch (error) {
      console.error(`Error deleting preference ${preferenceId}:`, error);
      return false;
    }
  },

  deactivateUserPreference: async (userId: string, preferenceId: number): Promise<boolean> => {
    try {
      await apiClient.put(`/chat/preferences/${preferenceId}/deactivate`, null, {
        params: { user_id: userId }
      });
      return true;
    } catch (error) {
      console.error(`Error deactivating preference ${preferenceId}:`, error);
      return false;
    }
  },

  activateUserPreference: async (userId: string, preferenceId: number): Promise<boolean> => {
    try {
      await apiClient.put(`/chat/preferences/${preferenceId}/activate`, null, {
        params: { user_id: userId }
      });
      return true;
    } catch (error) {
      console.error(`Error activating preference ${preferenceId}:`, error);
      return false;
    }
  },

  clearUserPreferences: async (userId: string): Promise<boolean> => {
    try {
      await apiClient.delete('/chat/preferences', {
        params: { user_id: userId }
      });
      return true;
    } catch (error) {
      console.error(`Error clearing preferences for user ${userId}:`, error);
      return false;
    }
  },

  // Dashboard auth
  dashboardLogin: async (email: string, password: string): Promise<DashboardLoginResponse> => {
    const response = await apiClient.post('/dashboard/auth/login', { email, password });
    const payload: DashboardLoginResponse = response.data;
    setDashboardToken(payload.token);
    return payload;
  },

  dashboardLogout: async (): Promise<void> => {
    try {
      await apiClient.post('/dashboard/auth/logout');
    } finally {
      setDashboardToken(null);
    }
  },

  dashboardMe: async (): Promise<{ user: DashboardUser; memberships: DashboardSite[] }> => {
    const response = await apiClient.get('/dashboard/auth/me');
    return response.data;
  },

  dashboardAcceptInvite: async (
    inviteToken: string,
    password: string,
    fullName?: string
  ): Promise<DashboardLoginResponse> => {
    const response = await apiClient.post('/dashboard/auth/accept-invite', {
      invite_token: inviteToken,
      password,
      full_name: fullName || null
    });
    const payload: DashboardLoginResponse = response.data;
    setDashboardToken(payload.token);
    return payload;
  },

  dashboardChangePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post('/dashboard/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword
    });
  },

  dashboardContactAdmin: async (payload: {
    subject?: string;
    message: string;
    site_id?: number;
  }): Promise<void> => {
    await apiClient.post('/dashboard/contact-admin', payload);
  },

  // Dashboard site/config APIs
  dashboardListSites: async (): Promise<DashboardSite[]> => {
    const response = await apiClient.get('/dashboard/sites');
    return response.data?.sites || [];
  },

  dashboardGetSite: async (siteId: number): Promise<DashboardSiteProfile> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}`);
    return response.data;
  },

  dashboardUpdateSite: async (
    siteId: number,
    payload: { name?: string; description?: string }
  ): Promise<DashboardSiteProfile> => {
    const response = await apiClient.patch(`/dashboard/sites/${siteId}`, payload);
    return response.data;
  },

  dashboardStartCrawl: async (
    siteId: number,
    payload: DashboardCrawlOptionsPayload
  ): Promise<{
    site_id: number;
    status: string;
    message: string;
    crawl_type: string;
    max_pages?: number;
    page_count?: number;
  }> => {
    const response = await apiClient.post(`/dashboard/sites/${siteId}/crawl`, payload);
    return response.data;
  },

  dashboardClaimSite: async (
    payload: DashboardClaimSitePayload
  ): Promise<{
    site_id: number;
    status: string;
    message: string;
    claimed: boolean;
    site?: DashboardSiteProfile;
  }> => {
    const response = await apiClient.post('/dashboard/sites/claim', payload);
    return response.data;
  },

  dashboardGetCrawlStatus: async (siteId: number): Promise<DashboardCrawlStatus> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/crawl/status`);
    return response.data;
  },

  dashboardListKnowledge: async (
    siteId: number
  ): Promise<{ site_id: number; entries: DashboardKnowledgeEntry[] }> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/knowledge`);
    return response.data;
  },

  dashboardCreateKnowledge: async (
    siteId: number,
    payload: { question: string; answer: string }
  ): Promise<{ site_id: number; entry: DashboardKnowledgeEntry }> => {
    const response = await apiClient.post(`/dashboard/sites/${siteId}/knowledge`, payload);
    return response.data;
  },

  dashboardUpdateKnowledge: async (
    siteId: number,
    entryId: string,
    payload: { question: string; answer: string }
  ): Promise<{ site_id: number; entry: DashboardKnowledgeEntry }> => {
    const response = await apiClient.put(`/dashboard/sites/${siteId}/knowledge/${entryId}`, payload);
    return response.data;
  },

  dashboardDeleteKnowledge: async (
    siteId: number,
    entryId: string
  ): Promise<{ site_id: number; entry_id: string; ok: boolean }> => {
    const response = await apiClient.delete(`/dashboard/sites/${siteId}/knowledge/${entryId}`);
    return response.data;
  },

  dashboardGetRevenueConfig: async (siteId: number): Promise<DashboardRevenueConfigResponse> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/revenue-config`);
    return response.data;
  },

  dashboardSaveRevenueConfig: async (
    siteId: number,
    config: DashboardRevenueConfigResponse['config']
  ): Promise<DashboardRevenueConfigResponse> => {
    const response = await apiClient.put(`/dashboard/sites/${siteId}/revenue-config`, config);
    return response.data;
  },

  dashboardGetMonthlyUsage: async (
    siteId: number,
    month?: string
  ): Promise<{ site_id: number; month: string; totals: SiteUsageSummaryTotals }> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/usage/summary/monthly`, {
      params: month ? { month } : undefined
    });
    return response.data;
  },

  dashboardGetInsights: async (
    siteId: number,
    month?: string
  ): Promise<DashboardSiteInsights> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/insights`, {
      params: month ? { month } : undefined,
    });
    return response.data;
  },

  dashboardGetBilling: async (siteId: number): Promise<DashboardSiteBilling> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/billing`);
    return response.data;
  },

  dashboardGetAccountBilling: async (): Promise<DashboardSiteBilling> => {
    const response = await apiClient.get('/dashboard/billing/account');
    return response.data;
  },

  dashboardCreateBillingCheckout: async (
    siteId: number,
    payload: { amount_usd: number; save_payment_method?: boolean }
  ): Promise<{ checkout_url: string; session_id: string; stripe_test_mode?: boolean }> => {
    const response = await apiClient.post(`/dashboard/sites/${siteId}/billing/checkout`, payload);
    return response.data;
  },

  dashboardCreateAccountBillingCheckout: async (payload: {
    amount_usd: number;
    save_payment_method?: boolean;
  }): Promise<{ checkout_url: string; session_id: string; stripe_test_mode?: boolean }> => {
    const response = await apiClient.post('/dashboard/billing/account/checkout', payload);
    return response.data;
  },

  dashboardConfirmBillingCheckout: async (
    siteId: number,
    sessionId: string
  ): Promise<{
    ok: boolean;
    payment_status?: string;
    session_id: string;
    stripe_test_mode?: boolean;
    billing: DashboardSiteBilling;
  }> => {
    const response = await apiClient.post(`/dashboard/sites/${siteId}/billing/confirm-checkout`, {
      session_id: sessionId,
    });
    return response.data;
  },

  dashboardConfirmAccountBillingCheckout: async (
    sessionId: string
  ): Promise<{
    ok: boolean;
    payment_status?: string;
    session_id: string;
    stripe_test_mode?: boolean;
    billing: DashboardSiteBilling;
  }> => {
    const response = await apiClient.post('/dashboard/billing/account/confirm-checkout', {
      session_id: sessionId,
    });
    return response.data;
  },

  dashboardSetupPaymentMethod: async (
    siteId: number
  ): Promise<{ checkout_url: string; session_id: string }> => {
    const response = await apiClient.post(`/dashboard/sites/${siteId}/billing/setup-payment-method`);
    return response.data;
  },

  dashboardSetupAccountPaymentMethod: async (): Promise<{
    checkout_url: string;
    session_id: string;
  }> => {
    const response = await apiClient.post('/dashboard/billing/account/setup-payment-method');
    return response.data;
  },

  dashboardUpdateAutoRecharge: async (
    siteId: number,
    payload: {
      enabled: boolean;
      threshold_usd?: number;
      charge_amount_usd?: number;
    }
  ): Promise<DashboardSiteBilling> => {
    const response = await apiClient.put(`/dashboard/sites/${siteId}/billing/auto-recharge`, payload);
    return response.data;
  },

  dashboardUpdateChatbotRunning: async (
    siteId: number,
    running: boolean
  ): Promise<DashboardSiteBilling> => {
    const response = await apiClient.put(`/dashboard/sites/${siteId}/billing/chatbot-running`, {
      running,
    });
    return response.data;
  },

  dashboardUpdateAccountAutoRecharge: async (payload: {
    enabled: boolean;
    threshold_usd?: number;
    charge_amount_usd?: number;
  }): Promise<DashboardSiteBilling> => {
    const response = await apiClient.put('/dashboard/billing/account/auto-recharge', payload);
    return response.data;
  },

  dashboardGetUsageRange: async (
    siteId: number,
    months = 6
  ): Promise<{ site_id: number; months: number; rows: Array<Record<string, any>>; totals: SiteUsageSummaryTotals }> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/usage/summary/monthly/range`, {
      params: { months }
    });
    return response.data;
  },

  dashboardListBookings: async (
    siteId: number,
    scope: 'upcoming' | 'past' | 'all' = 'upcoming',
    limit = 100
  ): Promise<{ site_id: number; scope: string; bookings: DashboardBooking[] }> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/bookings`, {
      params: { scope, limit },
    });
    return response.data;
  },

  dashboardUpdateBookingStatus: async (
    siteId: number,
    bookingId: number,
    status: 'cancelled' | 'no_show' | 'confirmed' | 'pending'
  ): Promise<{ ok: boolean; booking: DashboardBooking | null; calendar_warning?: string }> => {
    const response = await apiClient.patch(`/dashboard/sites/${siteId}/bookings/${bookingId}`, { status });
    return response.data;
  },

  dashboardGetCalendarConnection: async (
    siteId: number
  ): Promise<{ site_id: number; connection: DashboardCalendarConnection }> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/calendar`);
    return response.data;
  },

  dashboardDisconnectCalendar: async (
    siteId: number
  ): Promise<{
    ok: boolean;
    site_id?: number;
    booking_changes?: {
      can_book_in_chat: boolean;
      booking_enabled: boolean;
      booking_disabled_for_missing_fallback: boolean;
      cleared_in_chat_booking?: boolean;
    };
  }> => {
    const response = await apiClient.delete(`/dashboard/sites/${siteId}/calendar`);
    return response.data;
  },

  dashboardUpdateCalendarSettings: async (
    siteId: number,
    payload: { calendar_id?: string; event_type_uri?: string; scheduling_url?: string }
  ): Promise<{ ok: boolean; connection: DashboardCalendarConnection }> => {
    const response = await apiClient.put(`/dashboard/sites/${siteId}/calendar/settings`, payload);
    return response.data;
  },

  dashboardListCalendarOptions: async (
    siteId: number
  ): Promise<{ site_id: number; provider: string; options: DashboardCalendarOption[] }> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/calendar/options`);
    return response.data;
  },

  dashboardRetryBookingCalendarSync: async (
    siteId: number,
    bookingId: number
  ): Promise<{ ok: boolean; booking: DashboardBooking | null; calendar_warning?: string }> => {
    const response = await apiClient.post(`/dashboard/sites/${siteId}/bookings/${bookingId}/retry-calendar-sync`);
    return response.data;
  },

  dashboardCalendarConnectUrl: (siteId: number, provider: 'google' | 'calendly'): string => {
    const base = apiClient.defaults.baseURL || '';
    return `${base}/dashboard/sites/${siteId}/calendar/${provider}/connect`;
  },

  dashboardStartCalendarConnect: async (
    siteId: number,
    provider: 'google' | 'calendly'
  ): Promise<string> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/calendar/${provider}/connect`, {
      params: { json: true },
    });
    const redirectUrl = String(response.data?.redirect_url || '').trim();
    if (!redirectUrl) {
      throw new Error('Calendar connect did not return a redirect URL');
    }
    return redirectUrl;
  },

  dashboardGetEmailConnection: async (
    siteId: number
  ): Promise<{ site_id: number; connection: DashboardEmailConnection }> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/email`);
    return response.data;
  },

  dashboardStartEmailConnect: async (siteId: number): Promise<string> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/email/google/connect`, {
      params: { json: true },
    });
    const redirectUrl = String(response.data?.redirect_url || '').trim();
    if (!redirectUrl) {
      throw new Error('Email connect did not return a redirect URL');
    }
    return redirectUrl;
  },

  dashboardDisconnectEmail: async (siteId: number): Promise<{ ok: boolean }> => {
    const response = await apiClient.delete(`/dashboard/sites/${siteId}/email`);
    return response.data;
  },

  dashboardGetEmbedInfo: async (siteId: number): Promise<DashboardEmbedInfo> => {
    const response = await apiClient.get(`/dashboard/sites/${siteId}/embed`);
    return response.data;
  },

  dashboardUpdateEmbedInfo: async (
    siteId: number,
    payload: {
      script_snippet?: string | null;
      iframe_snippet?: string | null;
      widget_settings?: Record<string, any> | null;
      clear_custom?: boolean;
    }
  ): Promise<DashboardEmbedInfo> => {
    const response = await apiClient.put(`/dashboard/admin/sites/${siteId}/embed`, payload);
    return response.data;
  },

  // Dashboard admin APIs
  dashboardBootstrapAdmin: async (bootstrapKey: string, email: string, password: string, fullName?: string): Promise<any> => {
    const response = await apiClient.post(
      '/dashboard/auth/bootstrap-admin',
      {
        email,
        password,
        full_name: fullName || null
      },
      {
        headers: {
          'X-Dashboard-Bootstrap-Key': bootstrapKey
        }
      }
    );
    return response.data;
  },

  dashboardCreateInvite: async (
    email: string,
    siteId?: number,
    role: 'owner' | 'editor' | 'viewer' = 'viewer',
    expiresInHours = 72
  ): Promise<any> => {
    const response = await apiClient.post('/dashboard/admin/invites', {
      email,
      site_id: siteId ?? null,
      role,
      expires_in_hours: expiresInHours
    });
    return response.data;
  },

  dashboardListUsers: async (limit = 100): Promise<{ users: DashboardUser[] }> => {
    const response = await apiClient.get('/dashboard/admin/users', { params: { limit } });
    return response.data;
  },

  dashboardUpsertMembership: async (
    userId: number,
    siteId: number,
    role: 'owner' | 'editor' | 'viewer'
  ): Promise<any> => {
    const response = await apiClient.post('/dashboard/admin/memberships', {
      user_id: userId,
      site_id: siteId,
      role
    });
    return response.data;
  },

  dashboardGenerateResetToken: async (userId: number): Promise<{ reset_token: string; expires_at: string }> => {
    const response = await apiClient.post(`/dashboard/admin/users/${userId}/generate-reset-token`);
    return response.data;
  },

  dashboardForgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/dashboard/auth/forgot-password', { email });
  },

  dashboardResetPassword: async (resetToken: string, newPassword: string): Promise<void> => {
    await apiClient.post('/dashboard/auth/reset-password', {
      reset_token: resetToken,
      new_password: newPassword,
    });
  },
}; 